import {
  Box,
  Flex,
  Spacer,
  Skeleton,
  Heading,
  useToast,
} from '@chakra-ui/react';
import _ from 'lodash';
import React, { useState, useMemo } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { PortfolioChartDatePicker } from '../shared/PortfolioChartDatePicker';
import {
  chartsInitialEndDateJs,
  ChartTimePeriod,
  covenantsInitialStartDate,
  covenantsInitialStartDateJs,
  defaultSelectedTimePeriod,
  ExpandedPortfolioCovenantChartData,
  expandPortfolioCovenantsChartData,
  formatChartDataByReportingDate,
  formatCsvDownloadDataRows,
  onSelectDateRange,
  PortfolioChartConfig,
} from '../shared/portfolioChartUtils';
import { PortfolioDownloadCsvLink } from '../shared/PortfolioDownloadCsvLink';
import { PortfolioReChart } from '../shared/PortfolioReChart';
import { PortfolioTimePeriodSelectButtons } from '../shared/PortfolioTimePeriodSelectButtons';
import { PortfolioCovenantChartDetails } from './PortfolioCovenantChartDetails';
import {
  GetSuccessPortfolioCovenantsByCreditFacilityIdQueryGQLType,
  useGetSuccessPortfolioCovenantsByCreditFacilityIdQuery,
} from 'packages/borrowing-base/requests/portfolioCovenants.gql.generated';
import { colors } from 'packages/common-frontend/colors';
import { HighlightBox } from 'packages/common-frontend/components/HighlightBox';
import {
  getDefaultDashboardDisplayEndDate,
  errorToastConfig,
} from 'packages/common-frontend/constants';
import {
  getCovenantValueFormula,
  getThresholdLevel,
  getThresholdType,
  isThresholdValueIntermediateField,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/covenantValue/thresholdValueUtils';

const getThresholdLevelLineColors = (numberOfThresholds: number) => {
  if (numberOfThresholds <= 3) {
    return [colors.orange[300], colors.red[300], colors.red[500]];
  }
  if (numberOfThresholds <= 6) {
    return [
      colors.orange[300],
      colors.orange[500],
      colors.red[300],
      colors.red[400],
      colors.red[500],
      colors.red[600],
    ];
  }
  return [
    colors.orange[200],
    colors.orange[300],
    colors.orange[400],
    colors.orange[500],
    colors.orange[600],
    colors.red[200],
    colors.red[300],
    colors.red[400],
    colors.red[500],
    colors.red[600],
  ];
};

type PortfolioCovenantChartTemplateProps = {
  creditFacilityId: string;
  covenantKey: string;
  chartTitle: string;
  chartConfig: PortfolioChartConfig<any>;
  creditAgreementPortfolioCovenant:
    | CreditAgreement.DomainEntity.PortfolioCovenantProperty
    | undefined;
};
export const PortfolioCovenantChartTemplate = ({
  creditFacilityId,
  covenantKey,
  chartTitle,
  chartConfig,
  creditAgreementPortfolioCovenant,
}: PortfolioCovenantChartTemplateProps) => {
  const toast = useToast();

  const [
    selectedDataPoint,
    setSelectedDataPoint,
  ] = useState<ExpandedPortfolioCovenantChartData | null>(null);
  const [chartDataDateRange, setChartDataDateRange] = useState<{
    chartDataStartDate: Date;
    chartDataEndDate: Date;
  }>({
    chartDataStartDate: covenantsInitialStartDateJs,
    chartDataEndDate: chartsInitialEndDateJs,
  });

  // need both initial date range chart data (returned by query)
  // as well as chart data for time period
  // because we only want to filter chart data for time period on top of initial date range chart data
  // and not include a previously-filtered time period
  const [chartDataForDateRange, setChartDataForDateRange] = useState<
    ExpandedPortfolioCovenantChartData[]
  >([]);
  const [chartDataWithTimePeriod, setChartDataWithTimePeriod] = useState<
    ExpandedPortfolioCovenantChartData[]
  >([]);
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<ChartTimePeriod>(
    defaultSelectedTimePeriod,
  );

  const { dataRows, headers } = useMemo(
    () =>
      formatCsvDownloadDataRows<ExpandedPortfolioCovenantChartData>(
        chartDataWithTimePeriod,
      ),
    [chartDataWithTimePeriod],
  );

  const thresholdType =
    creditAgreementPortfolioCovenant &&
    creditAgreementPortfolioCovenant._config_
      ? getThresholdType(
          'portfolio',
          creditAgreementPortfolioCovenant._config_.intermediateFields,
        )
      : null;

  const portfolioCovenantValueFormula = getCovenantValueFormula({
    type: 'portfolio',
    creditAgreementCovenant: creditAgreementPortfolioCovenant,
  });

  const thresholdLevelsChartConfig = useMemo(() => {
    const thresholdChartConfig: PortfolioChartConfig<
      keyof ExpandedPortfolioCovenantChartData
    > = {};

    const thresholdIntermediateFieldKeys = _.chain(selectedDataPoint)
      .map((_property, key) =>
        isThresholdValueIntermediateField(key) ? key : undefined,
      )
      .compact()
      .value();

    thresholdIntermediateFieldKeys.sort().forEach((fieldKey, index) => {
      const thresholdLevel = getThresholdLevel(fieldKey);
      const thresholdChartColors = getThresholdLevelLineColors(
        thresholdIntermediateFieldKeys.length,
      );

      thresholdChartConfig[fieldKey] = {
        chartType: 'dashedLine',
        field: fieldKey,
        label: `Level ${thresholdLevel} Threshold`,
        format: 'percentage',
        color: thresholdChartColors[index] ?? colors.red[600],
      };
    });

    return thresholdChartConfig;
  }, [selectedDataPoint]);

  const chartConfigWithThresholds = {
    ...chartConfig,
    ...thresholdLevelsChartConfig,
  };

  const visibleChartComponents: (keyof ExpandedPortfolioCovenantChartData)[] = useMemo(() => {
    const thresholdLevelFields = _.compact(
      _.map(thresholdLevelsChartConfig, (config) => config?.field),
    );

    const standardFields = ['value'];

    return [...standardFields, ...thresholdLevelFields];
  }, [thresholdLevelsChartConfig]);

  const {
    refetch: refetchPortfolioCovenantForKey,
    loading: isPortfolioCovenantsLoading,
    data: portfolioCovenantsData,
  } = useGetSuccessPortfolioCovenantsByCreditFacilityIdQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
      start: covenantsInitialStartDate,
      end: getDefaultDashboardDisplayEndDate(),
      mode: 'RESULT',
    },
    onCompleted: (data) => {
      if (data && data.getSuccessPortfolioCovenantsByCreditFacilityId) {
        const dataForCovenantKey = data.getSuccessPortfolioCovenantsByCreditFacilityId.filter(
          (portCov) => portCov.covenantKey === covenantKey,
        );
        const expandedPortfolioCovenantData = expandPortfolioCovenantsChartData(
          dataForCovenantKey,
        );

        // we must make sure to set the core date range chart data with all of the data by day
        // this ensures that we have the base by-day data ready to use
        // when going back to a higher-frequency time period view than the currently selected period
        const formattedChartData = formatChartDataByReportingDate({
          chartData: expandedPortfolioCovenantData,
          selectedTimePeriod: ChartTimePeriod.day,
        });
        setChartDataForDateRange(formattedChartData);

        // then set data for the specific time period
        if (selectedTimePeriod !== ChartTimePeriod.day) {
          const formattedChartDataForPeriod = formatChartDataByReportingDate({
            chartData: expandedPortfolioCovenantData,
            selectedTimePeriod,
          });
          setChartDataWithTimePeriod(formattedChartDataForPeriod);
        } else {
          setChartDataWithTimePeriod(formattedChartData);
        }

        setSelectedDataPoint(formattedChartData[formattedChartData.length - 1]);
      }
    },
    notifyOnNetworkStatusChange: true,
  });

  if (!portfolioCovenantsData || isPortfolioCovenantsLoading) {
    return <Skeleton height="700px" />;
  }

  const onSelectTimePeriod = (timePeriod: ChartTimePeriod) => {
    setSelectedTimePeriod(timePeriod);

    const formattedChartData = formatChartDataByReportingDate({
      chartData: chartDataForDateRange,
      selectedTimePeriod: timePeriod,
    });

    setChartDataWithTimePeriod(formattedChartData);
  };

  const PortfolioChartInstance = PortfolioReChart<ExpandedPortfolioCovenantChartData>();

  return (
    <Box mb="40px">
      <HighlightBox backgroundColor={colors.millet[50]}>
        <Box
          backgroundColor="white"
          borderRadius="8px"
          borderWidth="1px"
          borderColor={colors.gray[200]}
          padding="40px"
        >
          <Heading as="h3" fontSize="20px" mb="24px">
            {chartTitle}
          </Heading>
          <Box width="100%">
            <Flex mb="40px">
              <PortfolioChartDatePicker
                onSelectDateRange={(
                  newDateRange: [Date | null, Date | null],
                ) => {
                  let refetchVariables;
                  if (newDateRange[0] && newDateRange[1]) {
                    refetchVariables = {
                      creditFacilityId,
                      start: commonDate.dateToFullDateString(newDateRange[0]),
                      end: commonDate.dateToFullDateString(newDateRange[1]),
                    };
                  }
                  if (!newDateRange[0] && !newDateRange[1]) {
                    refetchVariables = {
                      creditFacilityId,
                      start: covenantsInitialStartDate,
                      end: getDefaultDashboardDisplayEndDate(),
                    };
                  }
                  return onSelectDateRange<GetSuccessPortfolioCovenantsByCreditFacilityIdQueryGQLType>(
                    {
                      newDateRange,
                      refetch: refetchPortfolioCovenantForKey,
                      refetchVariables,
                      creditFacilityId,
                      setChartDataDateRange,
                    },
                  );
                }}
                currentDateRange={chartDataDateRange}
              />

              <Spacer />
              <PortfolioTimePeriodSelectButtons
                currentSelectedTimePeriod={selectedTimePeriod}
                onSelectTimePeriod={onSelectTimePeriod}
              />
            </Flex>

            {/* child 1 */}
            <PortfolioChartInstance
              chartData={chartDataWithTimePeriod}
              chartTitle={chartTitle}
              selectedTimePeriod={selectedTimePeriod}
              currentSelectedBarReportingDate={
                selectedDataPoint
                  ? selectedDataPoint.reportingDate
                  : getDefaultDashboardDisplayEndDate()
              }
              onSelectBar={setSelectedDataPoint}
              chartConfig={chartConfigWithThresholds}
              visibleChartComponents={visibleChartComponents}
            />
          </Box>
          <Spacer />
          {/* child 2 */}
          {selectedDataPoint && (
            <Box width="100%" mt="64px">
              <PortfolioCovenantChartDetails
                selectedDataPoint={selectedDataPoint}
                thresholdType={thresholdType}
                portfolioCovenantValueFormula={portfolioCovenantValueFormula}
              />

              <Flex>
                <Spacer />
                <PortfolioDownloadCsvLink
                  dataRows={dataRows}
                  headers={headers}
                  chartTitle={chartTitle}
                  selectedTimePeriod={selectedTimePeriod}
                />
              </Flex>
            </Box>
          )}
        </Box>
      </HighlightBox>
    </Box>
  );
};
